import { useState } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { deskServiceMembers, type ServiceDrill, type ServiceMood, type ServiceResponse } from './api'
import { bucketLabel, change, fmtCount } from './activity'
import { fmtDuration, fmtPct, moved, stack, waited, withinDay } from './service'
import { memberHref } from './links'

/**
 * Customer service on Activity: who got in touch, how, about what and how they felt.
 * Awareness only — nothing here answers or changes a message; each name opens the member's record.
 */

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const mono = 'font-mono'
const label = 'text-[13px] font-semibold text-paper/70'
const small = 'text-[12px] text-paper/60'
const btn = `inline-flex min-h-[38px] items-center rounded-[9px] border border-ink-400 bg-ink-700 px-3.5 text-[13px] font-semibold hover:bg-ink-600 ${focus}`

const CHANNELS = [
  { key: 'email', label: 'Email', fill: 'bg-radar-cyan' },
  { key: 'contact', label: 'Contact form', fill: 'bg-radar-violet' },
  { key: 'call', label: 'Phone calls', fill: 'bg-amber-glow' },
  { key: 'feedback', label: 'Feedback', fill: 'bg-radar-green' },
] as const
const MOODS: { key: ServiceMood; label: string; fill: string }[] = [
  { key: 'pleased', label: 'Pleased', fill: 'bg-radar-green' },
  { key: 'neutral', label: 'Neutral', fill: 'bg-ink-400' },
  { key: 'unhappy', label: 'Unhappy', fill: 'bg-radar-red' },
]

interface Range { from: string; to: string; compareLabel: string }

export function ServicePanel({ data, r, title, titleName, periodName, cancelled }: {
  data: ServiceResponse | undefined
  r: Range
  title: string
  titleName: string
  periodName: string
  /** The Cancelled card's figure, set beside cancel requests. */
  cancelled: number | null
}) {
  const [drill, setDrill] = useState<{ d: ServiceDrill; name: string } | null>(null)
  const [shown, setShown] = useState(50)
  const ctx = `${r.from}|${r.to}|${title}`
  const [drillCtx, setDrillCtx] = useState(ctx)
  const open = drill !== null && drillCtx === ctx

  const { data: rows, isFetching } = useQuery({
    queryKey: ['desk-service-members', drill?.d, r.from, r.to, title, shown],
    queryFn: () => deskServiceMembers(drill!.d, { from: r.from, to: r.to, publication: title }, shown),
    enabled: open,
    placeholderData: keepPreviousData,
  })

  const pick = (d: ServiceDrill, name: string) => {
    setDrill({ d, name }); setDrillCtx(ctx); setShown(50)
    setTimeout(() => document.getElementById('service-drill')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 60)
  }

  if (!data) {
    return (
      <section aria-labelledby="svc-h" className="flex min-w-0 flex-col gap-4 rounded-xl border border-ink-500 bg-ink-700 px-[22px] py-5">
        <h2 id="svc-h" className="m-0 text-[12px] font-bold uppercase tracking-[0.1em] text-paper/60">Customer service · {periodName}</h2>
        <p className="m-0 text-paper/60">Loading…</p>
      </section>
    )
  }

  const t = data.totals
  const c = data.compare_totals
  const ch = change(t.contacts, c ? c.contacts : 0)
  const channels = stack(CHANNELS.map((x) => ({ key: x.key, n: t.by_channel[x.key] })))
  const moods = stack(MOODS.map((x) => ({ key: x.key, n: t.moods[x.key] })))
  const wd = withinDay(t)
  const topMax = Math.max(1, ...data.topics.map((x) => x.count))
  const cancel = data.topics.find((x) => x.topic === 'cancel')
  const rate = (v: number) => v.toFixed(1)

  return (
    <section aria-labelledby="svc-h" className="flex min-w-0 flex-col gap-5 rounded-xl border border-ink-500 bg-ink-700 px-[22px] py-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="svc-h" className="m-0 text-[12px] font-bold uppercase tracking-[0.1em] text-paper/60">Customer service · {periodName}</h2>
        <span className={small}>Email, contact form, calls and feedback together{c ? ` · vs ${r.compareLabel}` : ''}</span>
      </div>

      {t.contacts === 0 && (!c || c.contacts === 0) ? (
        <p className="m-0 text-paper/60">No one got in touch in this period.</p>
      ) : (
        <div className="grid grid-cols-1 gap-7 lg:grid-cols-5">
          <div className="flex min-w-0 flex-col gap-5 lg:col-span-2">
            <div className="grid grid-cols-2 gap-4">
              <button type="button" onClick={() => pick({ what: 'contacts' }, 'Everyone who got in touch')} className={`flex flex-col gap-1 rounded-lg text-left ${focus}`}>
                <span className={label}>Members got in touch</span>
                <span className={`${mono} text-[40px] font-medium leading-tight tracking-tight`}>{fmtCount(t.contacts)}</span>
                <span className={small}>
                  {c && <><span className="font-semibold text-paper">{ch.number}</span> {ch.pct} · </>}
                  {fmtCount(t.members)} different {t.members === 1 ? 'member' : 'members'}
                </span>
                <span className="text-[12px] font-semibold text-amber-glow">See who →</span>
              </button>
              <div className="flex flex-col gap-1 border-l border-ink-500 pl-4">
                <span className={label}>Per 1,000 active members</span>
                <span className={`${mono} text-[40px] font-medium leading-tight tracking-tight`}>{data.per_1000 === null ? '—' : rate(data.per_1000)}</span>
                <span className={small}>
                  {[moved(data.per_1000, data.compare_per_1000, rate),
                    title !== 'all' && data.all_titles_per_1000 !== null ? `all titles ${rate(data.all_titles_per_1000)}` : '']
                    .filter(Boolean).join(' · ')}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className={label}>How they reached us</span>
              <Bar parts={channels.map((p) => ({ ...p, fill: CHANNELS.find((x) => x.key === p.key)!.fill }))} />
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[13px]">
                {CHANNELS.map((x) => (
                  <span key={x.key} className="flex items-center gap-2">
                    <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-sm ${x.fill}`} />{x.label}
                    <span className={`${mono} ml-auto font-semibold`}>{fmtCount(t.by_channel[x.key])}</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className={label}>How members felt</span>
              <Bar parts={moods.map((p) => ({ ...p, fill: MOODS.find((x) => x.key === p.key)!.fill }))} />
              <div className="flex flex-wrap gap-1.5">
                {MOODS.map((x) => (
                  <button key={x.key} type="button" onClick={() => pick({ what: 'contacts', mood: x.key }, x.label)}
                    className={`inline-flex min-h-[36px] items-center gap-2 rounded-full border border-ink-400 px-3 text-[13px] hover:bg-ink-600 ${focus}`}>
                    <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${x.fill}`} />{x.label}
                    <span className={`${mono} font-semibold`}>{fmtCount(t.moods[x.key])}</span>
                  </button>
                ))}
              </div>
              <span className={small}>
                {t.unhappy_share !== null && <>Unhappy: <span className="font-semibold text-paper">{fmtPct(t.unhappy_share)}</span>{c && c.unhappy_share !== null ? `, ${moved(t.unhappy_share, c.unhappy_share, fmtPct)}` : ''}</>}
                {t.moods.unknown > 0 && `${t.unhappy_share !== null ? ' · ' : ''}${fmtCount(t.moods.unknown)} not read yet`}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 border-t border-ink-500 pt-4">
              <div className="flex flex-col gap-1">
                <span className={label}>Answered within a day</span>
                <span className={`${mono} text-[24px]`}>{wd === null ? '—' : fmtPct(wd)}</span>
                <span className={small}>{c ? moved(wd, withinDay(c), fmtPct) : ''}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className={label}>First reply, typical</span>
                <span className={`${mono} text-[24px]`}>{t.first_reply_median_s === null ? '—' : fmtDuration(t.first_reply_median_s)}</span>
                <span className={small}>{c && c.first_reply_median_s !== null && t.first_reply_median_s !== null
                  ? `${t.first_reply_median_s <= c.first_reply_median_s ? 'quicker' : 'slower'} than ${fmtDuration(c.first_reply_median_s)}` : ''}</span>
              </div>
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-1 lg:col-span-3">
            <span className={`${label} mb-1.5`}>What it was about</span>
            {data.topics.map((x) => {
              const d = x.count - x.previous
              return (
                <button key={x.topic} type="button" onClick={() => pick({ what: 'contacts', topic: x.topic }, x.label)}
                  className={`grid min-h-[44px] grid-cols-[minmax(0,170px)_minmax(0,1fr)_40px_64px] items-center gap-3 rounded-lg px-2 text-left text-[14px] hover:bg-ink-600 sm:grid-cols-[minmax(0,190px)_minmax(0,1fr)_44px_72px_92px] ${focus}`}>
                  <span className="truncate">{x.label}</span>
                  <span className="h-2.5 rounded-[5px] bg-ink-600"><span className="block h-2.5 rounded-[5px] bg-paper/80" style={{ width: `${Math.round((x.count / topMax) * 100)}%` }} /></span>
                  <span className={`${mono} text-right font-semibold`}>{fmtCount(x.count)}</span>
                  <span className={`text-right text-[12px] ${d > 0 ? 'text-amber-glow' : 'text-paper/60'}`}>{c ? (d > 0 ? `▲ ${d}` : d < 0 ? `▼ ${-d}` : 'no change') : ''}</span>
                  <span className="hidden text-right text-[12px] text-paper/60 sm:block">{x.unhappy ? `${x.unhappy} unhappy` : ''}</span>
                </button>
              )
            })}
            {cancel && cancel.count > 0 && cancelled !== null && (
              <p className="m-0 mt-3 border-t border-ink-500 pt-3 text-[13px] text-paper/70">
                <span className="font-semibold text-paper">{fmtCount(cancel.count)}</span> asked to cancel or stop renewing; the Cancelled card shows <span className="font-semibold text-paper">{fmtCount(cancelled)}</span>.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-500 pt-3.5">
        <span className={small}>Click a figure, a mood or a topic to see who got in touch. Each name opens the member's record.</span>
        {data.waiting.count > 0 ? (
          <button type="button" onClick={() => pick({ what: 'waiting' }, 'Waiting for a reply')} className={`text-[12px] text-paper/60 underline decoration-paper/30 underline-offset-2 hover:text-paper ${focus}`}>
            {fmtCount(data.waiting.count)} waiting for a reply · oldest {waited(data.waiting.oldest_at!)}
          </button>
        ) : <span className={small}>Nothing waiting for a reply</span>}
      </div>

      {open && drill && (
        <div id="service-drill" className="flex scroll-mt-6 flex-col gap-3 border-t border-ink-500 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h3 className="m-0 text-[16px] font-bold">{drill.name}{rows ? `: ${fmtCount(rows.total)}` : ''}</h3>
              <span className={small}>{titleName} · {drill.d.what === 'waiting' ? 'now, whenever they arrived' : periodName}</span>
            </div>
            <button type="button" onClick={() => setDrill(null)} className={btn}>Close</button>
          </div>
          <div className="overflow-x-auto">
            <div aria-busy={isFetching} className="min-w-[700px]">
              <div className="grid grid-cols-[minmax(0,1.4fr)_90px_minmax(0,1fr)_80px_minmax(0,2fr)] gap-4 px-1 pb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-paper/60">
                <span>Who</span><span>Account</span><span>Title</span><span>Date</span><span>How it went</span>
              </div>
              {(rows?.items ?? []).map((d, i) => {
                const cls = 'grid grid-cols-[minmax(0,1.4fr)_90px_minmax(0,1fr)_80px_minmax(0,2fr)] items-center gap-4 border-t border-ink-500 px-1 py-2.5'
                const cells = (
                  <>
                    <span className="truncate font-semibold">{d.name || 'No name given'}</span>
                    <span className={`${mono} text-[13px]`}>{d.account_number ? `#${d.account_number}` : ''}</span>
                    <span>{d.publication_name ?? ''}</span>
                    <span className="text-paper/70">{bucketLabel(d.at.slice(0, 10), 'day')}</span>
                    <span className="text-paper/70">{d.detail}</span>
                  </>
                )
                const key = `${d.channel}-${d.at}-${i}`
                return d.subscriber_id != null
                  ? <Link key={key} to={memberHref(d.subscriber_id)} className={`${cls} hover:bg-ink-600 ${focus}`}>{cells}</Link>
                  : <div key={key} className={cls}>{cells}</div>
              })}
              {rows && rows.items.length === 0 && <p className="border-t border-ink-500 py-4 text-paper/60">No one.</p>}
            </div>
          </div>
          {rows && rows.total > rows.items.length && shown < 200 && (
            <button type="button" onClick={() => setShown(Math.min(200, shown + 50))} className={`${btn} self-start`}>
              Show more ({fmtCount(rows.items.length)} of {fmtCount(rows.total)})
            </button>
          )}
        </div>
      )}
    </section>
  )
}

function Bar({ parts }: { parts: { key: string; pct: number; fill: string }[] }) {
  if (parts.length === 0) return <div aria-hidden="true" className="h-3 rounded-md bg-ink-600" />
  return (
    <div aria-hidden="true" className="flex h-3 gap-0.5 overflow-hidden rounded-md">
      {parts.map((p) => <div key={p.key} className={p.fill} style={{ width: `${p.pct}%` }} />)}
    </div>
  )
}
